import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getIntakePrompts, upsertIntakePrompt } from './api';

export default function IntakePromptsEditor() {
  const [personas, setPersonas] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => getIntakePrompts().then((res) => {
    setPersonas(res.personas || []);
    setCanEdit(!!res.can_edit);
  }).catch(() => toast.error('Could not load intake prompts'));

  useEffect(() => { load(); }, []);

  const persona = personas[active];
  const setSteps = (steps) => setPersonas((ps) => ps.map((p, i) => (i === active ? { ...p, steps } : p)));
  const setLabel = (label) => setPersonas((ps) => ps.map((p, i) => (i === active ? { ...p, label } : p)));
  const editStep = (idx, field, value) => setSteps(persona.steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  const addStep = () => setSteps([...(persona.steps || []), { key: '', q: '', help: '' }]);
  const removeStep = (idx) => setSteps(persona.steps.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true);
    try {
      await upsertIntakePrompt(persona.persona, { label: persona.label, steps: persona.steps });
      toast.success('Saved');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!persona) return <div className="text-sm text-gray-500">Loading intake prompts...</div>;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">Guided intake prompts</h3>
        <p className="text-sm text-gray-500">The questions each persona answers when starting a new concept.</p>
      </div>

      <div className="flex gap-2">
        {personas.map((p, i) => (
          <button key={p.persona} onClick={() => setActive(i)}
            className={`text-xs px-3 py-1.5 rounded-full border ${i === active ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-500'}`}>
            {p.label}
          </button>
        ))}
      </div>

      <label className="block text-sm">
        <span className="text-gray-500">Persona label</span>
        <input value={persona.label || ''} disabled={!canEdit} onChange={(e) => setLabel(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm" />
      </label>

      <div className="space-y-3">
        {(persona.steps || []).map((s, idx) => (
          <div key={idx} className="rounded-xl border border-blue-100 dark:border-gray-700 p-3 space-y-2">
            <div className="flex gap-2">
              <input value={s.key} disabled={!canEdit} placeholder="key" onChange={(e) => editStep(idx, 'key', e.target.value)}
                className="w-40 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm" />
              <input value={s.q} disabled={!canEdit} placeholder="Question" onChange={(e) => editStep(idx, 'q', e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm" />
              {canEdit && <button onClick={() => removeStep(idx)} className="text-xs text-orange-600 px-2">Remove</button>}
            </div>
            <input value={s.help || ''} disabled={!canEdit} placeholder="Helper text" onChange={(e) => editStep(idx, 'help', e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm" />
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="flex gap-2">
          <button onClick={addStep} className="text-sm px-3 py-1.5 rounded-lg border border-blue-400 text-blue-700">Add question</button>
          <button onClick={save} disabled={busy} className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-50">
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
